import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from './Navbar';
import API_BASE_URL from '../api';

const OtherQuestionsInfo = () => {
  const [otherQuestionsInfo, setOtherQuestionsInfo] = useState({
    loveLanguage: '',
    zodiacSign: '',
    communicationStyle: 50, // Default midpoint
    conflictStyle: 50, // Default midpoint
  });

  const [questions, setQuestions] = useState({
    loveLanguage: 'Loading...',
    zodiacSign: 'Loading...',
    areYou: 'Loading...',
  });

  const [message, setMessage] = useState('');
  const [isNewUser] = useState(false);
  const [loading, setLoading] = useState(false);

  const getAuthToken = () => localStorage.getItem('stack_access_token');
  const navigate = useNavigate();

  const loveLanguages = [
    'Words of affirmation',
    'Acts of service',
    'Receiving gifts',
    'Quality time',
    'Physical touch',
  ];

  const zodiacSigns = [
    'Aries',
    'Taurus',
    'Gemini',
    'Cancer',
    'Leo',
    'Virgo',
    'Libra',
    'Scorpio',
    'Sagittarius',
    'Capricorn',
    'Aquarius',
    'Pisces',
  ];

  useEffect(() => {
    const token = getAuthToken();
    fetch(`${API_BASE_URL}/api/other-questions`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-Stack-Access-Token': token,
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.length > 0) {
          const parsedData = {
            loveLanguage: '',
            zodiacSign: '',
            communicationStyle: 50,
            conflictStyle: 50,
            loveLanguageQuestion: 'What is your love language?',
            zodiacSignQuestion: 'What is your zodiac sign?',
            areYouQuestion: 'Are you?',
          };

          data.forEach((item) => {
            if (item.question_text === 'What is your love language?') {
              parsedData.loveLanguage = item.answer_text || '';
            }
            if (item.question_text === 'What is your zodiac sign?') {
              parsedData.zodiacSign = item.answer_text || '';
            }
            if (item.question_text === 'Are you?') {
              const values = item.answer_text
                ? item.answer_text.split(', ')
                : ['50', '50'];
              parsedData.communicationStyle = parseInt(values[0], 10);
              parsedData.conflictStyle = parseInt(values[1], 10);
            }
          });

          setOtherQuestionsInfo({
            loveLanguage: parsedData.loveLanguage,
            zodiacSign: parsedData.zodiacSign,
            communicationStyle: parsedData.communicationStyle,
            conflictStyle: parsedData.conflictStyle,
          });

          setQuestions({
            loveLanguage: parsedData.loveLanguageQuestion,
            zodiacSign: parsedData.zodiacSignQuestion,
            areYou: parsedData.areYouQuestion,
          });
        }
      })
      .catch(() => setMessage('Error fetching other questions info.'));
  }, []);

  const handleSubmit = async () => {
    const token = getAuthToken();
    setLoading(true);

    const payload = {
      loveLanguage: otherQuestionsInfo.loveLanguage,
      zodiacSign: otherQuestionsInfo.zodiacSign,
      communicationStyle: otherQuestionsInfo.communicationStyle,
      conflictStyle: otherQuestionsInfo.conflictStyle,
    };

    const method = isNewUser ? 'POST' : 'PUT';

    try {
      const response = await fetch(`${API_BASE_URL}/api/other-questions`, {
        method: method,
        headers: {
          'X-Stack-Access-Token': token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Failed to update other questions info');
      }

      setMessage('Other questions info updated successfully.');
      setTimeout(() => navigate('/personality-test'), 2000);
    } catch {
      setMessage('Failed to update other questions info.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col px-4"
      style={{ backgroundColor: '#0D2329' }}
    >
      <Navbar />
      <div className="flex-1 flex justify-center items-center">
        <div
          className="w-full max-w-md p-10 rounded-lg shadow-md"
          style={{ backgroundColor: '#05664f' }}
        >
          <h1 className="text-4xl font-bold text-white text-center mb-8">
            Other Questions
          </h1>

          {message && (
            <p className="text-sm text-green-200 text-center mb-6">{message}</p>
          )}

          {/* Love Language Dropdown */}
          <div className="text-white mb-6">
            <label className="block text-sm font-medium mb-3">
              {questions.loveLanguage}
            </label>
            <select
              value={otherQuestionsInfo.loveLanguage}
              onChange={(e) =>
                setOtherQuestionsInfo({
                  ...otherQuestionsInfo,
                  loveLanguage: e.target.value,
                })
              }
              className="block w-full px-3 py-3 text-base border border-gray-300 text-black rounded-md"
            >
              <option value="">Select an option</option>
              {loveLanguages.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          {/* Zodiac Sign Dropdown */}
          <div className="text-white mb-6">
            <label className="block text-sm font-medium mb-3">
              {questions.zodiacSign}
            </label>
            <select
              value={otherQuestionsInfo.zodiacSign}
              onChange={(e) =>
                setOtherQuestionsInfo({
                  ...otherQuestionsInfo,
                  zodiacSign: e.target.value,
                })
              }
              className="block w-full px-3 py-3 text-base border border-gray-300 text-black rounded-md"
            >
              <option value="">Select an option</option>
              {zodiacSigns.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          {/* Sliders for Communication Style & Conflict Style */}
          <div className="text-white mb-8">
            <label className="block text-sm font-medium mb-3">
              {questions.areYou} (Communication Style)
            </label>
            <input
              type="range"
              min="0"
              max="100"
              step="50"
              value={otherQuestionsInfo.communicationStyle}
              onChange={(e) =>
                setOtherQuestionsInfo({
                  ...otherQuestionsInfo,
                  communicationStyle: parseInt(e.target.value, 10),
                })
              }
              className="w-full slider"
            />
            <div className="flex justify-between text-xs mt-3">
              <span>A big texter</span>
              <span>Both</span>
              <span>A phone call person</span>
            </div>
          </div>

          <div className="text-white mb-8">
            <label className="block text-sm font-medium mb-3">
              {questions.areYou} (Conflict Style)
            </label>
            <input
              type="range"
              min="0"
              max="100"
              step="50"
              value={otherQuestionsInfo.conflictStyle}
              onChange={(e) =>
                setOtherQuestionsInfo({
                  ...otherQuestionsInfo,
                  conflictStyle: parseInt(e.target.value, 10),
                })
              }
              className="w-full slider"
            />
            <div className="flex justify-between text-xs mt-3">
              <span>Quiet and reserved</span>
              <span>Both</span>
              <span>Confrontational</span>
            </div>
          </div>

          {/* Personality Test Info */}
          <div className="text-white text-center mb-6">
            <p className="text-lg font-bold">
              We have designed a personality test that will maximize your
              matching potential!
            </p>
          </div>

          {/* Buttons: Take It Now & Previous */}
          <div className="flex flex-col space-y-4">
            <button
              onClick={handleSubmit}
              className="w-full px-4 py-3 bg-white text-black font-semibold rounded-md hover:bg-gray-200 transition-all"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Take It Now!'}
            </button>

            <button
              onClick={() => navigate('/date-activities')}
              className="w-full px-4 py-3 bg-gray-500 text-white font-semibold rounded-md hover:bg-gray-600 transition-all"
            >
              Previous
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OtherQuestionsInfo;
